import { useMemo, useState } from "react";
import {
  Autocomplete, TextField, Button, Stack, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Typography,
} from "@mui/material";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

type Opt = { _id: string; fileName: string; cycleLabel: string };

export function StatementSwapControl({
  transaction, currentFileName, onSwapped,
}: {
  transaction: { _id: string; sourceFileId?: string | null; postingStatus?: string; qbImportStatus?: string } | null;
  currentFileName?: string | null;
  onSwapped?: () => void;
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Opt | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const options = (useQuery(api.sourceFiles.searchBankStatements, { search }) ?? []) as Opt[];
  const swap = useMutation(api.transactions.swapStatementAndReprocess);

  const isPosted = useMemo(() => {
    const p = transaction?.postingStatus;
    const q = transaction?.qbImportStatus;
    return p === "posted" || q === "imported" || q === "duplicate";
  }, [transaction]);

  const canSwap = !!transaction && !!selected && selected._id !== transaction.sourceFileId && !isPosted;

  async function doSwap() {
    if (!transaction || !selected) return;
    setBusy(true); setErr(null);
    try {
      await swap({
        transactionId: transaction._id as Id<"transactions">,
        newSourceFileId: selected._id as Id<"sourceFiles">,
      });
      setConfirmOpen(false);
      setSelected(null);
      onSwapped?.();
    } catch (e: any) {
      setErr(e?.message ?? "Failed to swap the statement");
    } finally {
      setBusy(false);
    }
  }

  if (!transaction) return null;

  const control = (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
      <Autocomplete
        size="small"
        sx={{ minWidth: 280 }}
        options={options}
        getOptionLabel={(o) => `${o.fileName} — ${o.cycleLabel}`}
        isOptionEqualToValue={(a, b) => a._id === b._id}
        value={selected}
        onChange={(_e, v) => setSelected(v)}
        onInputChange={(_e, v) => setSearch(v)}
        disabled={isPosted}
        renderInput={(params) => (
          <TextField {...params} label="Swap statement (search file)" />
        )}
      />
      <Button
        size="small" variant="outlined" disabled={!canSwap}
        onClick={() => setConfirmOpen(true)}
      >
        Swap &amp; reprocess
      </Button>
    </Stack>
  );

  return (
    <>
      {isPosted ? (
        <Tooltip title="Transaction already posted to QuickBooks — unpost before swapping the statement.">
          <span>{control}</span>
        </Tooltip>
      ) : control}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Swap statement &amp; reprocess</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2">Current statement: <strong>{currentFileName ?? "—"}</strong></Typography>
            <Typography variant="body2">New statement: <strong>{selected?.fileName ?? "—"}</strong></Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              The transaction will be re-evaluated by the pipeline (re-match + re-classification).
            </Typography>
            {err && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{err}</Typography>}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={doSwap} variant="contained" disabled={busy}>
            {busy ? "Reprocessing…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
